import React, { useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import axios from "axios";
import { useFlowStore } from "../../store/FlowStateStore.ts";
import { CanvasNode, CanvasNodeData } from "../../models/CanvasNode.ts";
import { Environment as environment } from "../../environment.ts";
import VariablePicker from "../Common/VariablePicker.tsx";
import LinkHandler from "./LinkHandler.tsx";
import TemplateBubble from "../Common/TemplateBubble.tsx";
import { ExternalLink, Phone } from "lucide-react";
import "react-toastify/dist/ReactToastify.css";
import { pruneStaleBodyEdge } from "../../utils/EdgeCleanup.ts";

const countPlaceholders = (text?: string | null): number => {
  return text?.match(/\{\{\s*\d+\s*\}\}/g)?.length ?? 0;
};

const splitTemplateParams = (headerText?: string | null, bodyText?: string | null, params: string[] = []) => {
  const headerCount = countPlaceholders(headerText);
  const bodyCount = countPlaceholders(bodyText);
  return {
    headerParams: params.slice(0, headerCount),
    bodyParams: params.slice(headerCount, headerCount + bodyCount),
  };
};

const formatDate = (date: string) => {
  const d = new Date(date);
  const day = d.getDate();
  const month = d.toLocaleString("default", { month: "short" });
  const year = d.getFullYear();
  const suffix =
    day % 10 === 1 && day !== 11
      ? "st"
      : day % 10 === 2 && day !== 12
        ? "nd"
        : day % 10 === 3 && day !== 13
          ? "rd"
          : "th";
  return `${day}${suffix} ${month} ${year}`;
};

const Template: React.FC<any> = (data) => {
  const [visible, setVisible] = useState(false);
  const [templates, setTemplates] = useState<any[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<any>(
    data?.nodeContent?.templateId
      ? { id: data.subNodeId, type: "TEMPLATE", data: data.nodeContent }
      : undefined
  );
  const { businessId, nodes, edges, setEdges } = useFlowStore();

  useEffect(() => {
    const payload = {
      businessId: businessId,
      page: 0,
      size: 100,
    };

    axios
      .put(`${environment.baseUrl}/message/template`, payload)
      .then((res) => {
        const template = res.data.data.data?.filter((template)=> (template.templateStatus == 'APPROVED'));
        setTemplates(template);
    })
      .catch(() => { });
  }, []);

  // The body-level connector lives at the node header now (see NodeHeader.tsx), not on
  // any individual bubble — but a stale wire from it can still end up pointing at a
  // non-delay target if the selected template changes buttons after the fact, so keep
  // pruning that here.
  useEffect(() => {
    pruneStaleBodyEdge(data.nodeId, edges, nodes, setEdges);
  }, [selectedTemplate]);

  return (
    <div className="node-body">
      {selectedTemplate ? (
        (() => {
          const d = selectedTemplate.data || {};
          const { headerParams, bodyParams } = splitTemplateParams(d.headerText, d.bodyText, d.templateParams);
          return (
            <div style={{ padding: "6px" }}>
              <TemplateBubble
                headerText={d.headerText}
                bodyText={d.bodyText}
                footerText={d.footerText}
                headerParams={headerParams}
                bodyParams={bodyParams}
                buttons={d.buttons}
                onClick={() => setVisible(true)}
                renderButtonExtra={(btn) => btn.type === "QUICK_REPLY" ? <LinkHandler idx={btn.text} /> : null}
              />
            </div>
          );
        })()
      ) : (
        <>
          <div
            className="font-label select-template"
            onClick={() => setVisible(true)}
          >
            Select Template
          </div>
        </>
      )}


          <Dialog
            header="Template Message"
            visible={visible}
            modal
            appendTo={document.body}
            maskStyle={{ background: "rgba(0,0,0,0.6)" }}
            style={{ width: "90vw" }}
            onHide={() => setVisible(false)}
          >
            <TemplateList
              setVisible={setVisible}
              templates={templates}
              data={data}
              setSelectedTemplate={setSelectedTemplate}
            />
          </Dialog>
    </div>
  );
};

export default Template;

const TemplateList: React.FC<any> = ({
  setVisible,
  templates,
  data,
  setSelectedTemplate,
}) => {
  const [previewTemplate, setPreviewTemplate] = useState<any>();
  const [showPreview, setShowPreview] = useState(false);
  const TABLE_HEADER: string[] = ["Name", "Category", "Status", "Type", "CreatedAt"];

  const selectTemplate = (data: any) => {
    setPreviewTemplate(data);
    setShowPreview(true);
  };

  return (
    <>
      <>
        <div className="template-table">
          <div className="table-header">
            { TABLE_HEADER.map((header)=> {return(
                <div>{header}</div>
              )}) }  
          </div>
          <div className="table-content">
            { templates.map((template)=> {
              return (
                <div onClick={()=> selectTemplate(template)}>
                  <div className="table-row">
                    <div>{template.name}</div>
                    <div>{template.category}</div>
                    <div className={template.isActive ? 'active-status' : 'inactive-status'}>{ template.isActive ? 'ACTIVE' : 'INACTIVE' }</div>
                    <div>{template.templateType}</div>
                    <div>
                      { formatDate(template.createdTimestamp) }
                    </div>
                  </div>
                </div>
              )
            }) }
          </div>
        </div>
      </>

      <Dialog
        header="Template Preview"
        visible={showPreview}
        modal
        appendTo={document.body}
        maskStyle={{ background: "rgba(0,0,0,0.6)" }}
        style={{ width: "50vw" }}
        onHide={() => setShowPreview(false)}
      >
        <TemplatePreview
          previewTemplate={previewTemplate}
          setVisible={setVisible}
          setShowSelectedTemplate={setShowPreview}
          setSelectedTemplate={setSelectedTemplate}
          data={data}
        />
      </Dialog>
    </>
  );
};

const TemplateInputRenderer = ({
  previewTemplate,
  formValues,
  setFormValues,
}) => {
  const handleChange = (key: string, value: string) => {
    setFormValues((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      {previewTemplate.components.map((component, compIdx) => {
        const { type, format, text, example, buttons } = component;

        const hasHeaderVars = type === "HEADER" && example?.header_text?.length > 0;
        const hasBodyVars = type === "BODY" && example?.body_text?.[0]?.length > 0;
        const hasButtonVars = type === "BUTTONS" && Array.isArray(buttons) &&
          buttons.some((b: any) => (b.type === "URL" && Array.isArray(b.example) && b.example.length > 0) || b.type === "COPY_CODE");

        // Dynamic media header: IMAGE / VIDEO / DOCUMENT with no defaultHeaderMediaUrl on the template
        const templateIsStatic = !!(previewTemplate as any)?.defaultHeaderMediaUrl;
        const hasDynamicMediaHeader = type === "HEADER" && !!format && format !== "TEXT"
          && format !== "LOCATION" && !templateIsStatic;

        if (type === "FOOTER") return null;
        if (type === "HEADER" && !hasHeaderVars && !hasDynamicMediaHeader) return null;
        if (type === "BUTTONS" && !hasButtonVars) return null;

        return (
          <div
            key={compIdx}
            style={{
              border: "1px solid #f1f5f9",
              borderRadius: "12px",
              padding: "12px",
              background: "#f8fafc"
            }}
          >
            <div className="flex justify-between items-center mb-2">
              <h4 style={{ fontSize: '10px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', margin: 0 }}>
                {type}{format ? ` (${format})` : ""}
              </h4>
            </div>

            {/* HEADER — text variables */}
            {hasHeaderVars && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {example.header_text.map((variable: string, idx: number) => {
                  const key = `header_${idx}`;
                  return (
                    <div key={idx} className="variable-input-row">
                      <div className="flex justify-between items-center mb-1">
                        <label style={{ fontSize: '11px', fontWeight: 700, color: '#2563eb' }}>{`{{${idx + 1}}}`}</label>
                        <VariablePicker onSelect={(val) => handleChange(key, (formValues[key] || "") + `{{${val}}}`)} />
                      </div>
                      <input
                        type="text"
                        className="premium-input-template"
                        placeholder="Enter value or select variable"
                        value={formValues[key] || ""}
                        onChange={(e) => handleChange(key, e.target.value)}
                        style={{
                          width: '100%',
                          padding: "6px 10px",
                          borderRadius: "8px",
                          border: "1px solid #e2e8f0",
                          background: 'white',
                          fontSize: '12px'
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            )}

            {/* HEADER — dynamic media URL (IMAGE / VIDEO / DOCUMENT) */}
            {hasDynamicMediaHeader && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <div className="flex justify-between items-center">
                  <label style={{ fontSize: '11px', fontWeight: 700, color: '#2563eb' }}>
                    {format} URL
                  </label>
                  <VariablePicker onSelect={(val) => handleChange('headerMedia', (formValues['headerMedia'] || '') + `{{${val}}}`)} />
                </div>
                <input
                  type="text"
                  className="premium-input-template"
                  placeholder="Paste URL or insert a variable (e.g. {{customer_image}})"
                  value={formValues['headerMedia'] || ""}
                  onChange={(e) => handleChange('headerMedia', e.target.value)}
                  style={{
                    width: '100%',
                    padding: "6px 10px",
                    borderRadius: "8px",
                    border: "1px solid #e2e8f0",
                    background: 'white',
                    fontSize: '12px'
                  }}
                />
                <span style={{ fontSize: '10px', color: '#64748b' }}>
                  The URL will be fetched by Meta at send time. Use an attribute to send a per-customer media.
                </span>
              </div>
            )}

            {/* BODY */}
            {type === "BODY" && (
               <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {hasBodyVars ? (
                    example.body_text[0].map((variable: string, idx: number) => {
                      const key = `body_${idx}`;
                      return (
                        <div key={idx} className="variable-input-row">
                          <div className="flex justify-between items-center mb-1">
                            <label style={{ fontSize: '11px', fontWeight: 700, color: '#2563eb' }}>{`{{${idx + 1}}}`}</label>
                            <VariablePicker onSelect={(val) => handleChange(key, (formValues[key] || "") + `{{${val}}}`)} />
                          </div>
                          <input
                            type="text"
                            className="premium-input-template"
                            placeholder="Enter value or select variable"
                            value={formValues[key] || ""}
                            onChange={(e) => handleChange(key, e.target.value)}
                            style={{
                              width: '100%',
                              padding: "6px 10px",
                              borderRadius: "8px",
                              border: "1px solid #e2e8f0",
                              background: 'white',
                              fontSize: '12px'
                            }}
                          />
                        </div>
                      );
                    })
                  ) : (
                    <span style={{ fontSize: '12px', color: '#334155' }}>{text}</span>
                  )}
               </div>
            )}

            {/* BUTTONS (URL with variables) */}
            {hasButtonVars && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {buttons.map((button: any, bIdx: number) => {
                  if (button.type === "COPY_CODE") {
                    return (
                      <div key={bIdx} style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <div style={{ fontSize: '11px', color: '#334155', fontWeight: 600 }}>
                          {button.text} — Coupon Code
                        </div>
                        <input
                          style={{ padding: '6px 10px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px' }}
                          placeholder="Enter coupon code"
                          value={formValues[`copycode_${bIdx}`] ?? ""}
                          onChange={(e) => handleChange(`copycode_${bIdx}`, e.target.value)}
                          onPointerDown={(e) => e.stopPropagation()}
                        />
                      </div>
                    );
                  }
                  if (button.type !== "URL" || !Array.isArray(button.example) || button.example.length === 0) return null;
                  return (
                    <div key={bIdx} style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <div style={{ fontSize: '11px', color: '#334155', fontWeight: 600 }}>
                        {button.text} — URL variable
                      </div>
                      {button.example.map((variable: string, idx: number) => {
                        const key = `button_${bIdx}_${idx}`;
                        return (
                          <div key={idx} className="variable-input-row">
                            <div className="flex justify-between items-center mb-1">
                              <label style={{ fontSize: '11px', fontWeight: 700, color: '#2563eb' }}>{`{{${idx + 1}}}`}</label>
                              <VariablePicker onSelect={(val) => handleChange(key, (formValues[key] || "") + `{{${val}}}`)} />
                            </div>
                            <input
                              type="text"
                              className="premium-input-template"
                              placeholder="Enter value or select variable"
                              value={formValues[key] || ""}
                              onChange={(e) => handleChange(key, e.target.value)}
                              style={{
                                width: '100%',
                                padding: "6px 10px",
                                borderRadius: "8px",
                                border: "1px solid #e2e8f0",
                                background: 'white',
                                fontSize: '12px'
                              }}
                            />
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const submitTemplate = (
  nodes,
  setNodes,
  data,
  templateContent,
  formValues,
  setShowSelectedTemplate,
  setVisible,
  setSelectedTemplate
) => {
  const orderedParams: string[] = [];
  const headerVars: string[] = [];
  const bodyVars: string[] = [];
  const buttonVars: string[] = [];
  templateContent.components.forEach((c: any) => {
    if (c.type === "HEADER" && c.example?.header_text) {
      c.example.header_text.forEach((_: any, idx: number) => {
        headerVars.push(String(formValues[`header_${idx}`] ?? ""));
      });
    }
    if (c.type === "BODY" && c.example?.body_text?.[0]) {
      c.example.body_text[0].forEach((_: any, idx: number) => {
        bodyVars.push(String(formValues[`body_${idx}`] ?? ""));
      });
    }
    if (c.type === "BUTTONS" && Array.isArray(c.buttons)) {
      c.buttons.forEach((b: any, bIdx: number) => {
        if (b.type === "URL" && Array.isArray(b.example)) {
          b.example.forEach((_: any, idx: number) => {
            buttonVars.push(String(formValues[`button_${bIdx}_${idx}`] ?? ""));
          });
        } else if (b.type === "COPY_CODE") {
          buttonVars.push(String(formValues[`copycode_${bIdx}`] ?? ""));
        }
      });
    }
  });
  orderedParams.push(...headerVars, ...bodyVars, ...buttonVars);

  const replacePlaceholders = (text: string = ""): string => {
    if (!text) return "";
    let replaced = text;
    bodyVars.forEach((value) => {
      replaced = replaced.replace(/{{\s*\d+\s*}}/, "[" + value + "]");
    });
    return replaced;
  };

  const updatedNodes = nodes.map((node) => {
    if (node.data.id === data.nodeId) {
      const updatedContent = (node.data as CanvasNodeData).content.map(
        (nodeContent) => {
          if (nodeContent.id === data.subNodeId) {
            const templateFullContent = {
              ...nodeContent,
              data: {
                bodyText: templateContent.components.find((c) => c.type === "BODY")
                  ?.text,
                footerText:
                  templateContent.components.find((c) => c.type === "FOOTER")
                    ?.text,
                headerText:
                  templateContent.components.find((c) => c.type === "HEADER")
                    ?.text,
                templateName: templateContent.name,
                quickReplies: [],
                buttons: (templateContent.components.find((c: any) => c.type === "BUTTONS")?.buttons || []).map((b: any) => ({
                  type: b.type,
                  text: b.text,
                  url: b.url ?? null,
                  phone_number: b.phone_number ?? null,
                })),
                callToAction: [],
                formattedButtons: [],
                parameters: orderedParams.length,
                sampleMessage: replacePlaceholders(
                  templateContent.components.find((c) => c.type === "BODY")
                    ?.text || ""
                ),
                status: templateContent.templateStatus,
                templateId: templateContent.templateId,
                templateLanguage: templateContent.readableLanguage,
                templateParams: orderedParams,
                type: templateContent.templateStatus,
                // Dynamic header media URL (IMAGE/VIDEO/DOCUMENT) — may contain {{attribute}} refs
                headerMediaUrl: String(formValues['headerMedia'] ?? ''),
              },
            };
            setSelectedTemplate(templateFullContent);
            return templateFullContent;
          }
          return nodeContent;
        }
      );

      return {
        ...node,
        data: {
          ...node.data,
          content: updatedContent,
        },
      };
    }
    return node;
  });

  setNodes([...(updatedNodes as CanvasNode[])]);

  setTimeout(() => {
    setShowSelectedTemplate(false);
    setVisible(false);
  }, 100);
};

const TemplatePreview: React.FC<any> = ({
  previewTemplate,
  setVisible,
  setShowSelectedTemplate,
  setSelectedTemplate,
  data,
}) => {
  const { nodes, setNodes } = useFlowStore();
  const [formValues, setFormValues] = useState({});

  return (
    <div style={{ display: "flex", gap: "20px", paddingBottom: '60px' }}>
      {/* Left - Input Parameters */}
      <div style={{ width: "55%" }}>
        <TemplateInputRenderer
          previewTemplate={previewTemplate}
          formValues={formValues}
          setFormValues={setFormValues}
        />
      </div>

      {/* Right - Live Preview */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <div style={{ fontSize: '9px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Preview</div>
        {(() => {
          const headerComp = previewTemplate.components.find((c: any) => c.type === "HEADER");
          const bodyComp = previewTemplate.components.find((c: any) => c.type === "BODY");
          const footerComp = previewTemplate.components.find((c: any) => c.type === "FOOTER");
          const buttonsComp = previewTemplate.components.find((c: any) => c.type === "BUTTONS");
          const headerCount = headerComp?.example?.header_text?.length || 0;
          const bodyCount = bodyComp?.example?.body_text?.[0]?.length || 0;
          const headerParams = Array.from({ length: headerCount }, (_, i) => String(formValues[`header_${i}`] ?? ""));
          const bodyParams = Array.from({ length: bodyCount }, (_, i) => String(formValues[`body_${i}`] ?? ""));
          const buttons = (buttonsComp?.buttons || []).map((b: any) => ({
            type: b.type, text: b.text, url: b.url ?? null, phone_number: b.phone_number ?? null
          }));
          return (
            <TemplateBubble
              headerText={headerComp?.text}
              bodyText={bodyComp?.text}
              footerText={footerComp?.text}
              headerParams={headerParams}
              bodyParams={bodyParams}
              buttons={buttons}
            />
          );
        })()}
      </div>

      <div style={{ position: "absolute", bottom: "0", left: 0, right: 0, padding: '12px 24px', borderTop: '1px solid #f1f5f9', background: '#f8fafc', display: 'flex', justifyContent: 'flex-end', borderBottomLeftRadius: '12px', borderBottomRightRadius: '12px' }}>
        <button
            className="save-config-btn"
            style={{
            padding: "8px 40px",
            background: "#2563eb",
            color: "white",
            border: "none",
            borderRadius: "8px",
            fontWeight: 700,
            cursor: 'pointer',
            fontSize: '14px'
            }}
            onClick={() =>
            submitTemplate(
                nodes,
                setNodes,
                data,
                previewTemplate,
                formValues,
                setShowSelectedTemplate,
                setVisible,
                setSelectedTemplate
            )
            }
        >
            Done
        </button>
      </div>
    </div>
  );
};
